import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    MenuItem,
    Popover,
    Select,
    Switch,
    Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../Firebase";
import { kProjectsCollectionName } from "../../utils/CollectionConstants";
import { TRANSCRIPTION_LANGUAGE_OPTIONS } from "./transcriptionLanguages";

type ProjectSettingsModalProps = {
    open: boolean;
    onClose: () => void;
    projectId: string | null | undefined;
    defaultTranscriptionLanguage: string | null | undefined;
    showWordlessAudioEventsOnScript: boolean;
    onDefaultTranscriptionLanguageChange?: (languageCode: string) => void;
    onShowWordlessAudioEventsOnScriptChange?: (value: boolean) => void;
};

const sidebarHeaderStyle = {
    fontFamily: "Inter",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "var(--app-text-muted)",
    marginTop: "4px",
};

const sidebarItemStyle = (active: boolean) => ({
    display: "flex",
    alignItems: "center",
    padding: "8px 10px",
    borderRadius: "8px",
    cursor: "default",
    backgroundColor: active ? "var(--app-bg-subtle)" : "transparent",
    color: active ? "var(--app-text-primary)" : "var(--app-text-muted)",
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: active ? 600 : 500,
    border: active ? "1px solid var(--app-border-subtle)" : "1px solid transparent",
});

const settingLabelStyle = {
    fontFamily: '"Inter", sans-serif',
    fontSize: "14px",
    fontWeight: 450,
    color: "#1F2937",
    // letterSpacing: "-0.01em",
};

const ProjectSettingsModal: React.FC<ProjectSettingsModalProps> = ({
    open,
    onClose,
    projectId,
    defaultTranscriptionLanguage,
    showWordlessAudioEventsOnScript,
    onDefaultTranscriptionLanguageChange,
    onShowWordlessAudioEventsOnScriptChange,
}) => {
    const [isSavingLanguage, setIsSavingLanguage] = useState(false);
    const [isSavingWordless, setIsSavingWordless] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);
    const [activeInfoKey, setActiveInfoKey] = useState<"language" | "wordless" | null>(null);

    useEffect(() => {
        if (!open) return;
        setError(null);
    }, [open]);

    const resolvedLanguage = useMemo(() => {
        const language = String(defaultTranscriptionLanguage ?? "").trim();
        return language || "auto";
    }, [defaultTranscriptionLanguage]);

    const persistProjectSettingsPatch = async (patch: Record<string, any>) => {
        const pid = String(projectId ?? "").trim();
        if (!pid) throw new Error("Project is not available.");
        const projectRef = doc(db, kProjectsCollectionName, pid);
        await updateDoc(projectRef, patch);
    };

    const handleLanguageChange = async (event: SelectChangeEvent<string>) => {
        const nextLanguage = String(event.target.value ?? "auto").trim() || "auto";
        const prevLanguage = resolvedLanguage;
        if (nextLanguage === prevLanguage) return;

        setError(null);
        setIsSavingLanguage(true);
        onDefaultTranscriptionLanguageChange?.(nextLanguage);
        try {
            await persistProjectSettingsPatch({ defaultTranscriptionLanguage: nextLanguage });
        } catch (e) {
            onDefaultTranscriptionLanguageChange?.(prevLanguage);
            setError("Could not save default transcription language.");
        } finally {
            setIsSavingLanguage(false);
        }
    };

    const handleWordlessToggle = async (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        const prev = Boolean(showWordlessAudioEventsOnScript);
        if (checked === prev) return;

        setError(null);
        setIsSavingWordless(true);
        onShowWordlessAudioEventsOnScriptChange?.(checked);
        try {
            await persistProjectSettingsPatch({ showWordlessAudioEventsOnScript: checked });
        } catch (e) {
            onShowWordlessAudioEventsOnScriptChange?.(prev);
            setError("Could not save script event visibility setting.");
        } finally {
            setIsSavingWordless(false);
        }
    };

    const openInfoPopover = (event: React.MouseEvent<HTMLElement>, key: "language" | "wordless") => {
        setInfoAnchorEl(event.currentTarget);
        setActiveInfoKey(key);
    };

    const closeInfoPopover = () => {
        setInfoAnchorEl(null);
        setActiveInfoKey(null);
    };

    const infoPopoverText =
        activeInfoKey === "language"
            ? "Applies to new track uploads in this project."
            : activeInfoKey === "wordless"
                ? "Show transcript tokens such as laughs and other non-word audio events."
                : "";

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    borderRadius: "18px",
                    width: "860px",
                    maxWidth: "calc(100vw - 40px)",
                    height: "64vh",
                    maxHeight: "620px",
                    backgroundColor: "var(--app-bg-canvas)",
                },
            }}
        >
            <DialogContent sx={{ padding: 0, height: "100%", position: "relative" }}>
                <Box sx={{ display: "flex", height: "100%" }}>
                    <Box
                        sx={{
                            width: "240px",
                            padding: "24px 20px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                        }}
                    >
                        <Typography sx={sidebarHeaderStyle}>Project</Typography>
                        <Box sx={sidebarItemStyle(true)}>
                            General
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1, padding: "20px 20px 20px 0" }}>
                        <Box
                            sx={{
                                height: "100%",
                                backgroundColor: "var(--app-bg-surface)",
                                borderRadius: "14px",
                                border: "1px solid var(--app-border-subtle)",
                                padding: "24px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "20px",
                                overflowY: "auto",
                            }}
                        >
                            <Typography sx={{ fontFamily: "Inter", fontSize: "22px", fontWeight: 600, color: "var(--app-text-primary)" }}>
                                Settings
                            </Typography>

                            {error ? (
                                <Alert severity="error" sx={{ borderRadius: "10px" }}>
                                    {error}
                                </Alert>
                            ) : null}

                            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", mt: 0.25 }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Typography sx={settingLabelStyle}>
                                            Default transcription language
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => openInfoPopover(e, "language")}
                                            sx={{ p: 0.35, color: "var(--app-text-muted)" }}
                                            aria-label="Default transcription language info"
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {isSavingLanguage ? <CircularProgress size={16} thickness={5} /> : null}
                                        <Select
                                            size="small"
                                            value={resolvedLanguage}
                                            onChange={handleLanguageChange}
                                            sx={{
                                                width: "170px",
                                                fontFamily: "Inter",
                                                fontSize: "13px",
                                                backgroundColor: "var(--app-bg-surface)",
                                                "& .MuiSelect-select": { py: 0.75, pr: 3.25 },
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxHeight: 320,
                                                        "& .MuiMenuItem-root": {
                                                            fontFamily: "Inter",
                                                            fontSize: "13px",
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            {TRANSCRIPTION_LANGUAGE_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Typography sx={settingLabelStyle}>
                                            Show wordless audio events on script
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => openInfoPopover(e, "wordless")}
                                            sx={{ p: 0.35, color: "var(--app-text-muted)" }}
                                            aria-label="Show wordless audio events info"
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {isSavingWordless ? <CircularProgress size={16} thickness={5} /> : null}
                                        <Switch
                                            checked={Boolean(showWordlessAudioEventsOnScript)}
                                            onChange={handleWordlessToggle}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Popover
                    open={Boolean(infoAnchorEl && activeInfoKey)}
                    anchorEl={infoAnchorEl}
                    onClose={closeInfoPopover}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{
                        sx: {
                            borderRadius: "10px",
                            border: "1px solid var(--app-border-subtle)",
                            boxShadow: "0px 8px 24px rgba(15, 23, 42, 0.12)",
                            px: 1.25,
                            py: 1,
                            maxWidth: "280px",
                        },
                    }}
                >
                    <Typography sx={{ fontFamily: "Inter", fontSize: "12px", color: "var(--app-text-muted)", lineHeight: 1.45 }}>
                        {infoPopoverText}
                    </Typography>
                </Popover>
            </DialogContent>
        </Dialog>
    );
};

export default ProjectSettingsModal;
